import React, { useCallback } from 'react';
import { Box, Typography, Paper, Divider } from '@mui/material';
import { BaseNode } from '../hooks/useNearbyBaseNodes';
import BaseNodeAccordion from './BaseNodeAccordion';
import InfoRow from './InfoRow';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import type { RootState } from '../store/store';
import { clearSelectedSite } from '../store/slices/servicePredictorSlice';

/**
 * Component to display site hierarchy information and base node details
 */
const SiteInfoPanel = () => {
  const dispatch = useAppDispatch();
  const site = useAppSelector(
    (state: RootState) => state.servicePredictor.selectedSite
  );

  const handleClose = useCallback(() => {
    dispatch(clearSelectedSite());
  }, [dispatch, site]);

  if (!site || site.baseNodes.length === 0) {
    return null;
  }

  const firstBaseNode = site.baseNodes[0];
  const operator = firstBaseNode.operator || null;
  const region = firstBaseNode.region || null;
  const market = firstBaseNode.market || null;
  const siteName = firstBaseNode.site || null;

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute',
        top: 280,
        right: 16,
        width: 500,
        maxHeight: 'calc(100vh - 360px)',
        overflowY: 'auto',
        zIndex: 2000,
        p: 2,
        pb: 4,
        boxSizing: 'border-box',
      }}
    >
      <Typography
        onClick={handleClose}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          cursor: 'pointer',
          color: 'text.secondary',
          fontSize: '1.2rem',
          lineHeight: 1,
          '&:hover': { color: 'text.primary' },
          zIndex: 1,
        }}
      >
        ✕
      </Typography>

      {/* Site Hierarchy Information */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}
        >
          Site Hierarchy
        </Typography>
        <InfoRow label="Site ID" value={site.siteId} />
        {operator && <InfoRow label="Operator" value={operator} />}
        {region && <InfoRow label="Region" value={region} />}
        {market && <InfoRow label="Market" value={market} />}
        {siteName && <InfoRow label="Site Name" value={siteName} />}
        <InfoRow
          label="Base Node Count"
          value={site.baseNodes.length.toString()}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Base Nodes - Expandable Views */}
      <Box>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}
        >
          Base Nodes ({site.baseNodes.length})
        </Typography>
        {site.baseNodes.map((bn, index) => {
          const isExpanded = site.selectedSerial === bn.serial;
          return (
            <BaseNodeAccordion
              key={bn.serial || index}
              baseNode={bn}
              index={index}
              isExpanded={isExpanded}
            />
          );
        })}
      </Box>
    </Paper>
  );
};

export default SiteInfoPanel;
